import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ObjectPanelGeometryContext } from '../types/object-panel-props'
import { selectionHasGeometryBlocked, selectionHasLocked } from './selection-protection'

/**
 * X/Y/W/H are inactive when the Text Tool is active and the selection
 * includes any shape (regular/irregular polygon, image, etc.).
 */
export function geometryFieldsInactiveForTextTool(
  context: ObjectPanelGeometryContext,
  activeObjects: ShapeData[]
): boolean {
  return context === 'text-tool' && activeObjects.length > 0
}

/**
 * Which panel controls are disabled: `panelControlsDisabled` (lock or the `disabled` prop) covers
 * appearance and path operations; `panelGeometryDisabled` additionally covers freeze, the text tool
 * and any geometry-blocked selection (X/Y/W/H and the link button); `rotateControlDisabled` also
 * requires something to rotate.
 */
export function resolvePanelDisabledState(params: {
  disabled: boolean
  geometryContext: ObjectPanelGeometryContext
  activeObject: ShapeData | null
  activeObjects: ShapeData[]
  shapesById: Map<string, ShapeData>
}): {
  panelControlsDisabled: boolean
  panelGeometryDisabled: boolean
  rotateControlDisabled: boolean
} {
  const { disabled, geometryContext, activeObject, activeObjects, shapesById } = params
  const totalSelectedCount = activeObjects.length
  const isMultiSelect = totalSelectedCount > 1
  const hasLockedSelection = selectionHasLocked(activeObjects, shapesById)
  const panelControlsDisabled = disabled || hasLockedSelection
  const panelGeometryDisabled =
    disabled ||
    hasLockedSelection ||
    geometryFieldsInactiveForTextTool(geometryContext, activeObjects) ||
    (totalSelectedCount > 0 && selectionHasGeometryBlocked(activeObjects, shapesById))
  const rotateControlDisabled =
    panelGeometryDisabled ||
    (!isMultiSelect && !activeObject) ||
    (isMultiSelect && activeObjects.length === 0)

  return { panelControlsDisabled, panelGeometryDisabled, rotateControlDisabled }
}
